import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Trophy, Medal, Crown, TrendingUp, Users, Info } from "lucide-react";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { User, UserModelData } from "../../../types";
import useThemeHook from "../../hooks/useThemeHook";
import CommonNavBar from "../../components/CommonNavBar";
import LoadingIndicator from "../../components/LoadingIndicator";
import Text from "../../components/ThemedText";

const LeaderboardPage: React.FC = () => {
  const theme = useThemeHook();
  const userData: UserModelData = useSelector((state: any) => state.actualUser);
  const [leaderboard, setLeaderboard] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLeaderboard = async () => {
    try {
      const topUsers = await arthereumProvider.getLeaderboard(20);
      setLeaderboard(topUsers);
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const topThree = leaderboard.slice(0, 3);
  const remainingList = leaderboard.slice(3);

  return (
    <div className={`min-h-screen pb-24 ${theme === "dark" ? "bg-gray-900" : "bg-slate-50"}`}>
      <CommonNavBar />
      
      <div className="max-w-md mx-auto px-4 mt-6">
        <div className="text-center mb-8">
          <h1 className={`text-3xl font-black tracking-tight ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
            Mining Giants
          </h1>
          <p className={`${theme === "dark" ? "text-gray-400" : "text-slate-500"} text-sm mt-1`}>
            Real-time global rankings 🌍
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <LoadingIndicator />
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="text-center py-20">
             <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-slate-300" />
             </div>
             <p className="text-slate-400 font-medium">No miners found yet.</p>
             <p className="text-slate-500 text-xs mt-1">Be the first to claim a grant and lead the way!</p>
          </div>
        ) : (
          <>
            {/* Podium Section */}
            <div className="flex items-end justify-center gap-2 mb-12 h-64">
              {/* Rank 2 */}
              {topThree[1] && (
                <motion.div 
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex flex-col items-center flex-1"
                >
                  <div className="relative mb-2">
                    <div className="w-16 h-16 rounded-2xl bg-slate-200 border-4 border-slate-300 overflow-hidden shadow-sm">
                        <img src={topThree[1].profileImage || ""} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute -top-3 -right-3 bg-amber-600 text-white text-[10px] font-black px-2 py-1 rounded-full shadow-lg border-2 border-white">
                      3x
                    </div>
                  </div>
                  <div className="w-full bg-slate-300 rounded-t-2xl h-24 flex flex-col items-center justify-center shadow-inner pt-2">
                    <Medal className="text-slate-500 mb-1" size={20} />
                    <span className="text-xs font-bold text-slate-600 truncate px-2 w-full text-center flex items-center justify-center gap-1">
                      {topThree[1].username}
                      {(topThree[1].isLifetimePro || (topThree[1].isPro && topThree[1].proExpiry && new Date((topThree[1].proExpiry as any).seconds * 1000) > new Date())) && "👑"}
                    </span>
                  </div>
                </motion.div>
              )}

              {/* Rank 1 */}
              {topThree[0] && (
                <motion.div 
                  initial={{ y: 80, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="flex flex-col items-center flex-1"
                >
                  <div className="relative mb-3">
                    <div className="w-20 h-20 rounded-2xl bg-amber-50 border-4 border-amber-400 overflow-hidden shadow-xl ring-4 ring-amber-400/20">
                        <img src={topThree[0].profileImage || ""} alt="" className="w-full h-full object-cover" />
                    </div>
                    <Crown className="absolute -top-6 left-1/2 -translate-x-1/2 text-amber-500 fill-amber-500 drop-shadow-md" size={24} />
                    <div className="absolute -top-1 -right-4 bg-red-600 text-white text-xs font-black px-2.5 py-1 rounded-full shadow-xl border-2 border-white animate-bounce">
                      5x
                    </div>
                  </div>
                  <div className="w-full bg-gradient-to-b from-amber-400 to-amber-500 rounded-t-3xl h-36 flex flex-col items-center justify-center shadow-lg pt-4">
                    <Trophy className="text-white mb-2" size={28} />
                    <span className="text-sm font-black text-white truncate px-2 w-full text-center drop-shadow-sm flex items-center justify-center gap-1">
                      {topThree[0].username}
                      {(topThree[0].isLifetimePro || (topThree[0].isPro && topThree[0].proExpiry && new Date((topThree[0].proExpiry as any).seconds * 1000) > new Date())) && "👑"}
                    </span>
                    <span className="text-[10px] font-bold text-white/80 uppercase">The Mining King</span>
                  </div>
                </motion.div>
              )}

              {/* Rank 3 */}
              {topThree[2] && (
                <motion.div 
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="flex flex-col items-center flex-1"
                >
                  <div className="relative mb-2">
                    <div className="w-16 h-16 rounded-2xl bg-slate-100 border-4 border-orange-200 overflow-hidden shadow-sm">
                        <img src={topThree[2].profileImage || ""} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute -top-3 -right-3 bg-amber-600 text-white text-[10px] font-black px-2 py-1 rounded-full shadow-lg border-2 border-white">
                      3x
                    </div>
                  </div>
                  <div className="w-full bg-orange-100/80 rounded-t-2xl h-20 flex flex-col items-center justify-center shadow-inner pt-2">
                    <Medal className="text-orange-500 mb-1" size={20} />
                    <span className="text-xs font-bold text-orange-600 truncate px-2 w-full text-center flex items-center justify-center gap-1">
                      {topThree[2].username}
                      {(topThree[2].isLifetimePro || (topThree[2].isPro && topThree[2].proExpiry && new Date((topThree[2].proExpiry as any).seconds * 1000) > new Date())) && "👑"}
                    </span>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Rules Alert */}
            <div className="bg-indigo-600 rounded-2xl p-4 mb-8 flex items-center gap-3 text-white shadow-lg shadow-indigo-600/20">
              <div className="bg-white/20 p-2 rounded-xl">
                <TrendingUp size={24} />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-sm">Podium Rewards Active! ⚡</h4>
                <p className="text-[11px] text-indigo-100">Top 1 gets <span className="font-bold">5x Mining Power</span>. Top 2-3 get <span className="font-bold">3x</span>. Climb the ranks!</p>
              </div>
            </div>

            {/* Rankings List */}
            <div className={`${theme === "dark" ? "bg-gray-800 border-gray-700" : "bg-white border-slate-200"} rounded-3xl border overflow-hidden shadow-sm`}>
              <div className={`px-6 py-4 border-b flex justify-between items-center ${theme === "dark" ? "bg-gray-800/50 border-gray-700" : "bg-slate-50/50 border-slate-100"}`}>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Rankings</span>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Balance</span>
              </div>
              
              <div className={`divide-y ${theme === "dark" ? "divide-gray-700" : "divide-slate-50"}`}>
                {leaderboard.map((user, idx) => {
                  const isUserPro = user.isLifetimePro || (user.isPro && user.proExpiry && new Date((user.proExpiry as any).seconds * 1000) > new Date());
                  
                  return (
                    <div key={user.id} className={`flex items-center justify-between px-6 py-4 transition-colors group ${theme === "dark" ? "hover:bg-gray-750" : "hover:bg-slate-50"}`}>
                      <div className="flex items-center gap-4">
                        <span className={`text-sm font-black w-6 ${idx < 3 ? 'text-indigo-600' : 'text-slate-300'}`}>
                          {idx + 1}
                        </span>
                        <div className={`w-10 h-10 rounded-xl overflow-hidden border-2 shadow-sm ${
                          isUserPro 
                            ? "border-amber-400 ring-2 ring-amber-400/20" 
                            : theme === "dark" ? "bg-gray-700 border-gray-600" : "bg-slate-100 border-slate-200"
                        }`}>
                          <img src={user.profileImage || ""} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex flex-col">
                          <span className={`text-sm font-bold flex items-center gap-1 ${theme === "dark" ? "text-white" : "text-slate-800"}`}>
                            {user.username}
                            {idx === 0 && <Crown size={12} className="text-amber-500 fill-amber-500" />}
                            {isUserPro && (
                              <div className="flex items-center gap-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-[8px] font-black text-white px-1.5 py-0.5 rounded-full shadow-sm ml-1">
                                PRO 👑
                              </div>
                            )}
                          </span>
                          <span className="text-[10px] text-slate-400 uppercase tracking-tighter">
                            {isUserPro ? "Elite Infinity Member" : "Verified Miner"}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-black ${theme === "dark" ? "text-white" : "text-slate-900"}`}>{user.arthereumBalance?.toFixed(3)}</div>
                        <div className="text-[10px] text-indigo-500 font-bold uppercase">ATH Tokens</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="text-center py-8 text-xs text-slate-400 flex items-center justify-center gap-1">
              <Info size={12} />
              Rankings refresh every 5 minutes.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPage;
